import { Fragment, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CountryConfig, StationV2, UrlV2 } from "@orvis/core";
import type { FirestoreLike } from "@orvis/data";
import { EmptyRow, LoadingRow, NotLive, StationCard } from "../components/ui.js";
import { StationPlayer } from "../components/players/index.js";
import { PlayerControls } from "../components/players/PlayerControls.js";
import { useTvMode } from "../hooks/useTvMode.js";
import { InlineAd } from "../components/InlineAd.js";
import { BrowseFilters } from "../components/BrowseFilters.js";
import { ReportButton } from "../components/ReportButton.js";
import { ZapButtons } from "../components/ZapButtons.js";
import { CastButton, CastingBanner } from "../components/PlayerExtras.js";
import { inlineAdAfter } from "../ads.js";
import { useRadios } from "../queries.js";
import { useNowPlaying } from "../state/nowPlaying.js";
import { useFavorites } from "../state/favorites.js";
import { useRecentlyPlayed } from "../state/recentlyPlayed.js";
import { formatSleepRemaining, useSleepTimer } from "../state/sleepTimer.js";
import { useMediaSession } from "../hooks/useMediaSession.js";
import { useBrowseFilter } from "../hooks/useBrowseFilter.js";
import { useGeolocation } from "../hooks/useGeolocation.js";
import { sameStation, useStationList, zapTarget } from "../hooks/useStationList.js";
import { useZapGestures } from "../hooks/useZapGestures.js";
import { useNowPlayingTitle } from "../hooks/useNowPlayingTitle.js";
import { useCast } from "../hooks/useCast.js";
import { useBackgroundAudio } from "../hooks/useBackgroundAudio.js";

export function RadioScreen({ db, country }: { db: FirestoreLike; country: CountryConfig }) {
  const { t } = useTranslation();
  const q = useRadios(db, country.code);
  const now = useNowPlaying();
  const favorites = useFavorites();
  const recent = useRecentlyPlayed();
  const sleep = useSleepTimer();
  const [filter, setFilter] = useBrowseFilter();
  const geo = useGeolocation();
  const list = useStationList(q.data?.open, filter, geo.coords);

  const [mediaEl, setMediaEl] = useState<HTMLMediaElement | null>(null);
  const [activeUrl, setActiveUrl] = useState<UrlV2 | undefined>();
  const songTitle = useNowPlayingTitle(country.code, now.radio, activeUrl, mediaEl, now.radioPaused);
  const cast = useCast({ station: now.radio, url: activeUrl, kind: "audio", el: mediaEl, subtitle: songTitle });
  const tvMode = useTvMode();

  // "Near me" needs a location fix; ask the first time the chip is on.
  const { request: requestLocation } = geo;
  useEffect(() => {
    if (filter.near) void requestLocation();
  }, [filter.near, requestLocation]);

  const zap = useCallback(
    (dir: 1 | -1) => {
      const target = zapTarget(list, now.radio, dir);
      if (!target) return;
      recent.push("radio", target.key);
      now.playRadio(target.station);
    },
    [list, now, recent]
  );
  useZapGestures(!!now.radio && !now.tv, () => zap(1), () => zap(-1));

  // Media Session — surfaces the current radio station to the lockscreen,
  // with working pause/resume so backgrounding the app doesn't kill playback.
  useMediaSession(now.radio, {
    paused: now.radioPaused,
    onPause: () => now.pauseRadio(),
    onResume: () => now.resumeRadio(),
    onStop: () => now.playRadio(undefined),
    onNext: () => zap(1),
    onPrev: () => zap(-1),
    nowPlaying: songTitle,
  });

  // Android: foreground service + media notification so radio survives
  // the app being backgrounded.
  useBackgroundAudio(now.radio, {
    enabled: !tvMode,
    paused: now.radioPaused,
    songTitle,
    onPlay: () => now.resumeRadio(),
    onPause: () => now.pauseRadio(),
    onStop: () => now.playRadio(undefined),
    onNext: () => zap(1),
    onPrev: () => zap(-1),
  });

  // Sleep timer — when it expires, stop whatever's playing.
  useEffect(() => {
    if (sleep.didExpire) {
      now.playRadio(undefined);
      sleep.acknowledgeExpiry();
    }
  }, [sleep.didExpire, sleep, now]);

  if (q.isPending) return <LoadingRow label={t("radio")} />;
  const data = q.data;
  if (!data || data.live === false) return <NotLive />;

  return (
    <div className="p-3 space-y-3">
      <RadioPlayerCard
        country={country}
        station={now.radio}
        paused={now.radioPaused}
        songTitle={songTitle}
        onTogglePause={() => (now.radioPaused ? now.resumeRadio() : now.pauseRadio())}
        onClose={() => now.playRadio(undefined)}
        onZap={zap}
        onElement={setMediaEl}
        media={mediaEl}
        onActiveUrl={setActiveUrl}
        activeUrl={activeUrl}
        cast={cast}
      />
      <SleepTimerRow />
      <BrowseFilters
        stations={data.open}
        filter={filter}
        onChange={setFilter}
        showRegions
        geoStatus={geo.status}
      />
      <div className="space-y-2">
        {list.length === 0 ? <EmptyRow label={t("nothing_here_yet")} /> : null}
        {list.map(({ station, key }, idx) => (
          <Fragment key={key}>
            <StationCard
              station={station}
              subtitle={station.region}
              selected={sameStation(now.radio, station)}
              isFavorite={favorites.isFavorite("radio", key)}
              onToggleFavorite={() => favorites.toggle("radio", key)}
              onSelect={() => {
                recent.push("radio", key);
                now.playRadio(station);
              }}
            />
            {inlineAdAfter(idx, list.length) ? <InlineAd country={country} slot="radio" /> : null}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function RadioPlayerCard({
  country,
  station,
  paused,
  songTitle,
  onTogglePause,
  onClose,
  onZap,
  onElement,
  media,
  onActiveUrl,
  activeUrl,
  cast,
}: {
  country: CountryConfig;
  station: StationV2 | undefined;
  paused: boolean;
  songTitle: string | undefined;
  onTogglePause: () => void;
  onClose: () => void;
  onZap: (dir: 1 | -1) => void;
  onElement: (el: HTMLMediaElement | null) => void;
  media: HTMLMediaElement | null;
  onActiveUrl: (u: UrlV2 | undefined) => void;
  activeUrl: UrlV2 | undefined;
  cast: ReturnType<typeof useCast>;
}) {
  const { t } = useTranslation();
  const tv = useTvMode();
  const report = (compact: boolean) =>
    station ? (
      <ReportButton country={country.code} kind="radio" station={station} url={activeUrl?.value} compact={compact} />
    ) : null;
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate">{station?.name ?? t("select_radio")}</div>
          {songTitle ? (
            <div className="text-xs text-accent truncate" aria-live="polite">
              <span aria-hidden>♪ </span>
              {songTitle}
            </div>
          ) : station?.description ? (
            <div className="text-xs text-text/60 truncate">{station.description}</div>
          ) : null}
        </div>
        {station ? (
          <>
            <ZapButtons onPrev={() => onZap(-1)} onNext={() => onZap(1)} />
            {cast.available ? <CastButton onClick={cast.open} active={cast.casting} device={cast.device} /> : null}
            {report(true)}
            <button onClick={onClose} className="text-sm text-text/70 hover:text-text px-2 py-1">
              {t("stop")}
            </button>
          </>
        ) : null}
      </div>
      {cast.casting ? <CastingBanner device={cast.device} /> : null}
      {station ? (
        <StationPlayer
          station={station}
          mode="audio"
          paused={paused || cast.casting}
          nativeControls={!tv}
          onElement={onElement}
          onActiveUrl={onActiveUrl}
          failedActions={report(false)}
        />
      ) : null}
      {station && tv ? <PlayerControls media={media} paused={paused} onTogglePause={onTogglePause} /> : null}
    </div>
  );
}

const OPTIONS = [0, 15, 30, 60] as const;

function SleepTimerRow() {
  const { t } = useTranslation();
  const sleep = useSleepTimer();

  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2">
      <div className="flex items-center gap-2 text-xs">
        <span className="text-text/60">{t("sleep_timer")}</span>
        <div className="flex-1 flex gap-1 flex-wrap">
          {OPTIONS.map((mins) => {
            const active = sleep.minutes === mins;
            return (
              <button
                key={mins}
                type="button"
                onClick={() => sleep.set(mins)}
                className={
                  `px-2 py-1 rounded border ` +
                  (active
                    ? "bg-primary border-accent text-text"
                    : "border-border text-text/70 hover:text-text")
                }
              >
                {mins === 0
                  ? t("sleep_off")
                  : mins === 15
                  ? t("sleep_15min")
                  : mins === 30
                  ? t("sleep_30min")
                  : t("sleep_60min")}
              </button>
            );
          })}
        </div>
        {sleep.remainingSeconds > 0 ? (
          <span className="text-text/60 tabular-nums">
            {t("sleep_active")} {formatSleepRemaining(sleep.remainingSeconds)}
          </span>
        ) : null}
      </div>
    </div>
  );
}
